import React, { useState } from "react";
import { useMutation } from "react-query";
import * as Sentry from "@sentry/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { updateEmployerUser } from "../api/user";
import Button from "./library/Button";
import styled from "styled-components";
import Divider from "./library/Divider";
import {
  ImageUploadField,
  RichTextField,
  TextInputField,
  SubmitButton,
  FormError,
} from "./library/Form";
import { addHttpsPrefix } from "../helpers";
import { updateEmployerCompany } from "../api/company";
import { useFlash } from "../hooks";
import FlashMessage from "./shared/general/FlashMessage";
import { EmployerCriticalField } from "../types";
import Employer from "../apiResources/Employer";
import CurrentUser from "../apiResources/CurrentUser";
import EmployerUser from "../apiResources/EmployerUser";

const CriticalEmployerSettingsForm = (props: {
  missingFields: EmployerCriticalField[];
  user: CurrentUser["attributes"] | EmployerUser["attributes"];
  company: Employer["attributes"];
}) => {
  const { missingFields, user } = props;

  const [error, setError] = useState("");
  const [focusedOption, setFocusedOption] = useState(0);
  const flash = useFlash();

  const mutationOptions = {
    onSuccess: () => {
      nextOption();
      flash.show();
    },
    onError: (error) => {
      setError("Something went wrong");
      Sentry.captureException(error);
    },
  };

  const updateCompany = useMutation(updateEmployerCompany, mutationOptions);

  const updateUser = useMutation(updateEmployerUser, mutationOptions);

  const prevOption = () => {
    if (focusedOption === 0) {
      window.location.href = "/employer/jobs";
    }

    setFocusedOption((val) => Math.max(val - 1, 0));
  };

  const nextOption = () => {
    const lastOption = missingFields.length - 1;
    if (focusedOption === lastOption) {
      window.location.href = "/employer/jobs";
    }

    setFocusedOption((val) => Math.min(val + 1, lastOption));
  };

  const isShown = (field: EmployerCriticalField) => {
    return missingFields[focusedOption] === field;
  };

  const submitForm = async ({
    websiteAddress,
    linkedinUrl,
    avatar,
    ...rest
  }) => {
    if (avatar) {
      await updateUser.mutateAsync({
        id: user.id,
        photo: avatar,
      });
      return;
    }

    const values = Object.entries(rest).reduce((all, [key, value]) => {
      if (value) {
        all[key] = value;
      }

      return all;
    }, {});

    await updateCompany.mutateAsync({
      id: user.company.id,
      websiteAddress: websiteAddress
        ? addHttpsPrefix(websiteAddress)
        : undefined,
      linkedinUrl: linkedinUrl ? addHttpsPrefix(linkedinUrl) : undefined,
      ...values,
    });
  };

  const shouldSaveForm = (values) => {
    return (
      (isShown("website") && values.websiteAddress) ||
      (isShown("social") && values.linkedinUrl) ||
      (isShown("numberOfEmployees") && values.numberOfEmployees) ||
      (isShown("logo") && values.logo) ||
      (isShown("avatar") && values.avatar) ||
      (isShown("location") && values.townOrCity) ||
      (isShown("profile") && values.profile)
    );
  };

  const isLastOption = focusedOption === missingFields.length - 1;

  return (
    <>
      <FlashMessage
        showFlashMessage={flash.visible}
        success
        message="Successfully updated your settings"
      />
      <Divider />
      <Formik
        initialValues={{
          websiteAddress: "",
          linkedinUrl: "",
          numberOfEmployees: null,
          logo: null,
          avatar: null,
          townOrCity: "",
          profile: null,
        }}
        validationSchema={Yup.object({
          numberOfEmployees: Yup.number()
            .typeError("Please enter a number")
            .integer("Please enter a number without decimals")
            .nullable(true),
        })}
        onSubmit={submitForm}
      >
        {({ values }) => (
          <Form id="critical-employer-settings">
            {error && <FormError>{error}</FormError>}
            {isShown("profile") && (
              <RichTextField
                label="Tell us more about your company?"
                name="profile"
              />
            )}
            {isShown("logo") && (
              <ImageUploadField
                label="Upload your company logo so people can easily see who they are working with"
                name="logo"
              />
            )}
            {isShown("avatar") && (
              <ImageUploadField
                label="Upload a profile image so people can recognise you"
                name="avatar"
                avatar
              />
            )}
            {isShown("website") && (
              <TextInputField
                label="Add your website address so agencies can easily access additional information about your company"
                name="websiteAddress"
                prefixDisplay="https://"
              />
            )}
            {isShown("bannerImages") && (
              <ImageUploadField
                label="Add an image to display as the background on your company profile"
                name="employerProfileImage"
              />
            )}
            {isShown("social") && (
              <TextInputField
                label="Add your LinkedIn so recruiters can see more about your company"
                name="linkedinUrl"
                prefixDisplay="https://"
              />
            )}
            {isShown("location") && (
              <TextInputField
                label="Tell us where your head office is based?"
                name="townOrCity"
                placeholder="Manchester"
              />
            )}
            {isShown("numberOfEmployees") && (
              <TextInputField
                label="How many employees have you currently got?"
                name="numberOfEmployees"
              />
            )}
            <FormFooter>
              <Button tertiary onClick={prevOption}>
                {focusedOption === 0 ? "Cancel" : "Previous"}
              </Button>
              <ProgressIndicator>
                {focusedOption + 1} of {missingFields.length}
              </ProgressIndicator>
              {shouldSaveForm(values) ? (
                <SubmitButton>
                  {isLastOption ? "Save & Finish" : "Save & Next"}
                </SubmitButton>
              ) : (
                <Button primary onClick={nextOption}>
                  {isLastOption ? "Finish" : "Next"}
                </Button>
              )}
            </FormFooter>
          </Form>
        )}
      </Formik>
    </>
  );
};

const ProgressIndicator = styled.div`
  color: ${({ theme }) => theme.colors.muted};
  font-size: 14px;
  text-align: center;
`;

const FormFooter = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default CriticalEmployerSettingsForm;
